import React, { useState, useRef, useEffect } from 'react';

interface DualBarDataPoint {
  date: string;
  income: number;
  expenses: number;
  label: string;
}

interface DualBarChartProps {
  data: DualBarDataPoint[];
  height?: number;
  incomeColor?: string;
  expensesColor?: string;
}

function DualBarChart({
  data,
  height = 300,
  incomeColor = '#059669',
  expensesColor = '#dc2626',
}: DualBarChartProps): JSX.Element | null {
  const [hoveredBar, setHoveredBar] = useState<DualBarDataPoint | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState({ width: 800, height });
  const containerRef = useRef<HTMLDivElement>(null);

  // Update dimensions on resize (ResizeObserver for accuracy)
  useEffect(() => {
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      const w = el.getBoundingClientRect().width;
      setDimensions({ width: Math.max(w, 400), height });
    };

    // Initial measure after mount/paint
    const raf = requestAnimationFrame(update);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      if (containerRef.current) ro.observe(containerRef.current);
    }
    window.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(raf);
      if (ro && containerRef.current) {
        ro.unobserve(containerRef.current);
        ro.disconnect();
      }
      window.removeEventListener('resize', update);
    };
  }, [height]);

  if (!data || data.length === 0) return null;

  // Calculate dimensions
  const padding = { top: 30, right: 50, bottom: 50, left: 80 };
  const chartWidth = dimensions.width - padding.left - padding.right;
  const chartHeight = dimensions.height - padding.top - padding.bottom;

  // Find max value for scaling
  const allValues = data.flatMap((d) => [d.income, d.expenses]);
  const maxValue = Math.max(0, ...allValues);

  // Scale functions
  const periodWidth = chartWidth / data.length;
  const barWidth = (periodWidth * 0.8) / 2; // Each bar takes 40% of period width
  const barSpacing = periodWidth * 0.1; // 10% spacing between periods
  const scaleX = (index: number) => padding.left + index * periodWidth + barSpacing / 2;
  const scaleY = (value: number): number => {
    const denom = maxValue === 0 ? 1 : maxValue;
    return padding.top + chartHeight - (value / denom) * chartHeight;
  };

  // Generate grid lines
  const gridLines = [];
  for (let i = 0; i <= 5; i += 1) {
    const y = padding.top + (i / 5) * chartHeight;
    const value = maxValue - (i / 5) * maxValue;
    gridLines.push(
      <g key={i}>
        <line
          x1={padding.left}
          y1={y}
          x2={padding.left + chartWidth}
          y2={y}
          stroke={i === 0 || i === 5 ? '#e5e7eb' : '#f9fafb'}
          strokeWidth={i === 0 || i === 5 ? 1 : 0.5}
        />
        <text
          x={padding.left - 15}
          y={y + 5}
          textAnchor="end"
          fontSize="11"
          fill="#9ca3af"
          fontWeight="500">
          €{Math.round(value).toLocaleString()}
        </text>
      </g>,
    );
  }

  // Generate x-axis labels with smart spacing
  const xAxisLabels = [];
  const labelInterval = Math.max(1, Math.floor(data.length / 8));

  for (let i = 0; i < data.length; i += labelInterval) {
    const x = scaleX(i) + barWidth;
    xAxisLabels.push(
      <text
        key={i}
        x={x}
        y={dimensions.height - 15}
        textAnchor="middle"
        fontSize="10"
        fill="#9ca3af"
        fontWeight="500">
        {data[i].label}
      </text>,
    );
  }

  // Handle mouse events
  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    setMousePosition({ x, y });

    // Find hovered bar
    const chartX = x - padding.left;
    if (chartX >= 0 && chartX <= chartWidth) {
      const periodIndex = Math.floor(chartX / periodWidth);
      if (periodIndex >= 0 && periodIndex < data.length) {
        setHoveredBar(data[periodIndex]);
      }
    }
  };

  const handleMouseLeave = () => {
    setHoveredBar(null);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={dimensions.width}
        height={dimensions.height}
        className="h-full w-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}>
        {/* Grid lines */}
        {gridLines}

        {/* X-axis */}
        <line
          x1={padding.left}
          y1={dimensions.height - padding.bottom}
          x2={padding.left + chartWidth}
          y2={dimensions.height - padding.bottom}
          stroke="#e5e7eb"
          strokeWidth={1}
        />

        {/* Y-axis */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={dimensions.height - padding.bottom}
          stroke="#e5e7eb"
          strokeWidth={1}
        />

        {/* Dual Bars */}
        {data.map((d, i) => {
          const incomeY = scaleY(d.income);
          const expensesY = scaleY(d.expenses);
          const incomeHeight = padding.top + chartHeight - incomeY;
          const expensesHeight = padding.top + chartHeight - expensesY;
          const isHovered = hoveredBar === d;

          return (
            <g key={i}>
              {/* Income Bar */}
              <rect
                x={scaleX(i)}
                y={incomeY}
                width={barWidth}
                height={incomeHeight}
                fill={incomeColor}
                opacity={isHovered ? 0.8 : 0.7}
                stroke={isHovered ? incomeColor : 'none'}
                strokeWidth={isHovered ? 2 : 0}
                rx={2}
                ry={2}
              />
              {/* Expenses Bar */}
              <rect
                x={scaleX(i) + barWidth}
                y={expensesY}
                width={barWidth}
                height={expensesHeight}
                fill={expensesColor}
                opacity={isHovered ? 0.8 : 0.7}
                stroke={isHovered ? expensesColor : 'none'}
                strokeWidth={isHovered ? 2 : 0}
                rx={2}
                ry={2}
              />
            </g>
          );
        })}

        {/* X-axis labels */}
        {xAxisLabels}
      </svg>

      {/* Tooltip */}
      {hoveredBar && (
        <div
          className="pointer-events-none absolute z-10 min-w-[180px] rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
          style={{
            left: Math.min(mousePosition.x + 15, dimensions.width - 200),
            top: Math.max(mousePosition.y - 100, 10),
          }}>
          <div className="mb-3 text-sm font-semibold text-gray-900">{hoveredBar.label}</div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: incomeColor }} />
                <span className="text-sm text-gray-600">Income</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                €{hoveredBar.income.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: expensesColor }} />
                <span className="text-sm text-gray-600">Expenses</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                €{hoveredBar.expenses.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DualBarChart;
